// DESCRIPTION: Email settings tab component for configuring booking confirmation
// and reminder email notifications in the Nemtly Booking admin settings.

import { __ } from '@wordpress/i18n';
import {
    TextControl,
    ToggleControl,
    TextareaControl,
    Card,
    CardBody,
    CardHeader,
    __experimentalDivider as Divider,
} from '@wordpress/components';

/**
 * Email Settings Component
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.settings      Current settings object.
 * @param {Function} props.updateSetting  Callback to update a single setting key.
 *
 * @return {JSX.Element}
 */
function EmailSettings({ settings, updateSetting }) {
    // Default confirmation message with proper placeholders
    const defaultConfirmationMessage = `Dear {attendee_name},

Thank you for your booking! Your reservation has been confirmed for the following event:

Event: {event_title}
Date: {event_date}
Time: {event_time}
Location: {event_location}
Booking Reference: {booking_uuid}

We look forward to seeing you at the event. If you have any questions, please don't hesitate to contact us.

Best regards,
{site_name}

---
This is an automated confirmation email. Please do not reply to this message.`;

    // Default confirmation subject
    const defaultConfirmationSubject = 'Booking Confirmation - {event_title}';

    // Default reminder subject
    const defaultReminderSubject = 'Reminder: {event_title} tomorrow';

    // Default reminder message
    const defaultReminderMessage = `Dear {attendee_name},

This is a friendly reminder about your upcoming event:

Event: {event_title}
Date: {event_date}
Time: {event_time}
Location: {event_location}
Booking Reference: {booking_uuid}

We look forward to seeing you tomorrow!

Best regards,
{site_name}

---
This is an automated reminder email. Please do not reply to this message.`;

    return (
        <div className="nb-space-y-6">
            {/* Email Settings Card */}
            <Card className="nb-border nb-border-slate-200 nb-shadow-sm">
                <CardHeader className="nb-bg-slate-50">
                    <div className="nb-flex nb-items-center nb-gap-3">
                        <div className="nb-w-8 nb-h-8 nb-bg-slate-600 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
                            <svg className="nb-w-4 nb-h-4 nb-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="nb-text-lg nb-font-semibold nb-text-slate-900">{__('Email Notifications', 'nemtly-booking')}</h3>
                            <p className="nb-text-sm nb-text-slate-600">{__('Configure email notifications for bookings and reminders', 'nemtly-booking')}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="nb-space-y-6">
                    <ToggleControl
                        label={__('Enable Email Notifications', 'nemtly-booking')}
                        checked={settings.email_notifications_enabled || false}
                        onChange={(value) => updateSetting('email_notifications_enabled', value)}
                        help={__('Send email notifications for bookings', 'nemtly-booking')}
                    />

                    {settings.email_notifications_enabled && (
                        <div className="nb-space-y-6">
                            {/* Email Configuration */}
                            <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-3 nb-gap-6">
                                <TextControl
                                    label={__('Admin Email', 'nemtly-booking')}
                                    value={settings.admin_email || ''}
                                    onChange={(value) => updateSetting('admin_email', value)}
                                    type="email"
                                    placeholder={__('admin@example.com', 'nemtly-booking')}
                                    help={settings.admin_email ?
                                        __('Admin email is set', 'nemtly-booking') :
                                        __('Email address to receive booking notifications', 'nemtly-booking')
                                    }
                                />

                                <TextControl
                                    label={__('From Name', 'nemtly-booking')}
                                    value={settings.from_name || ''}
                                    onChange={(value) => updateSetting('from_name', value)}
                                    placeholder={__('Your Company Name', 'nemtly-booking')}
                                    help={settings.from_name ?
                                        __('From name is set', 'nemtly-booking') :
                                        __('Name that appears in the "From" field', 'nemtly-booking')
                                    }
                                />

                                <TextControl
                                    label={__('From Email', 'nemtly-booking')}
                                    value={settings.from_email || ''}
                                    onChange={(value) => updateSetting('from_email', value)}
                                    type="email"
                                    placeholder={__('noreply@example.com', 'nemtly-booking')}
                                    help={__('Email address that appears in the "From" field', 'nemtly-booking')}
                                />
                            </div>

                            <Divider />

                            {/* Confirmation Email */}
                            <div className="nb-space-y-4">
                                <div className="nb-flex nb-items-center nb-gap-2 nb-mb-4">
                                    <div className="nb-w-2 nb-h-2 nb-bg-green-500 nb-rounded-full"></div>
                                    <h4 className="nb-text-base nb-font-medium nb-text-slate-900">{__('Confirmation Email', 'nemtly-booking')}</h4>
                                </div>

                                <TextControl
                                    label={__('Subject Line', 'nemtly-booking')}
                                    value={settings.confirmation_subject || defaultConfirmationSubject}
                                    onChange={(value) => updateSetting('confirmation_subject', value)}
                                    help={__('Available placeholders: {event_title}, {attendee_name}, {site_name}', 'nemtly-booking')}
                                />

                                <TextareaControl
                                    label={__('Message Template', 'nemtly-booking')}
                                    value={settings.confirmation_message || defaultConfirmationMessage}
                                    onChange={(value) => updateSetting('confirmation_message', value)}
                                    rows={8}
                                    help={__('Available placeholders: {attendee_name}, {event_title}, {event_date}, {event_time}, {event_location}, {booking_uuid}, {site_name}', 'nemtly-booking')}
                                />
                            </div>

                            <Divider />

                            {/* Reminder Email */}
                            <div className="nb-space-y-4">
                                <ToggleControl
                                    label={__('Enable Reminder Emails', 'nemtly-booking')}
                                    checked={settings.reminder_enabled || false}
                                    onChange={(value) => updateSetting('reminder_enabled', value)}
                                    help={__('Send reminder emails before events', 'nemtly-booking')}
                                />

                                {settings.reminder_enabled && (
                                    <div className="nb-space-y-4 nb-pl-6">
                                        <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-4">
                                            <TextControl
                                                label={__('Reminder Hours Before Event', 'nemtly-booking')}
                                                value={settings.reminder_hours || '24'}
                                                onChange={(value) => updateSetting('reminder_hours', parseInt(value) || 24)}
                                                type="number"
                                                min="1"
                                                max="168"
                                                help={__('How many hours before the event to send the reminder', 'nemtly-booking')}
                                            />
                                        </div>

                                        <TextControl
                                            label={__('Subject Line', 'nemtly-booking')}
                                            value={settings.reminder_subject || defaultReminderSubject}
                                            onChange={(value) => updateSetting('reminder_subject', value)}
                                            help={__('Available placeholders: {event_title}, {attendee_name}, {site_name}', 'nemtly-booking')}
                                        />

                                        <TextareaControl
                                            label={__('Message Template', 'nemtly-booking')}
                                            value={settings.reminder_message || defaultReminderMessage}
                                            onChange={(value) => updateSetting('reminder_message', value)}
                                            rows={8}
                                            help={__('Available placeholders: {attendee_name}, {event_title}, {event_date}, {event_time}, {event_location}, {booking_uuid}, {site_name}', 'nemtly-booking')}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

export default EmailSettings;
